import { createFileRoute, Link } from "@tanstack/react-router";
import { AppShell } from "@/components/AppShell";
import { lenders, recommendationMeta, type Recommendation } from "@/lib/mock-data";
import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import { z } from "zod";

const searchSchema = z.object({
  q: z.string().optional().catch(""),
  filter: z.enum(["all", "safe", "compare", "high-risk", "avoid"]).optional().catch("all"),
});

export const Route = createFileRoute("/lenders/")({
  head: () => ({
    meta: [
      { title: "Lender Search — LendyTrack" },
      { name: "description", content: "Search thousands of lenders. Independent trust scores, complaint analysis, and predatory-pattern detection." },
      { property: "og:title", content: "Lender Search — LendyTrack" },
      { property: "og:description", content: "Find safer lenders. Independent scoring, no kickbacks." },
      { property: "og:url", content: "/lenders" },
    ],
    links: [{ rel: "canonical", href: "/lenders" }],
  }),
  validateSearch: searchSchema,
  component: LenderSearch,
});

function LenderSearch() {
  const { q: initialQ, filter: initialFilter } = Route.useSearch();
  const [q, setQ] = useState(initialQ ?? "");
  const [filter, setFilter] = useState<Recommendation | "all">(initialFilter ?? "all");

  const results = useMemo(() => {
    const text = q.trim().toLowerCase();
    return lenders.filter((l) => {
      if (filter !== "all" && l.recommendation !== filter) return false;
      if (!text) return true;
      return l.name.toLowerCase().includes(text) || l.category.toLowerCase().includes(text);
    });
  }, [q, filter]);

  return (
    <AppShell>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Lender intelligence</div>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">Search lenders</h1>
        <p className="mt-1 text-sm text-muted-foreground">Independent trust scores, complaint analysis, and predatory-pattern detection.</p>
      </div>

      <div className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-3 shadow-soft md:flex-row md:items-center">
        <div className="flex flex-1 items-center gap-2 rounded-xl bg-muted px-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by lender name or category"
            className="w-full bg-transparent py-3 text-sm focus:outline-none"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(["all", "safe", "compare", "high-risk", "avoid"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold capitalize transition ${
                filter === f ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-secondary"
              }`}
            >
              {f === "all" ? "All" : recommendationMeta[f].label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 text-xs text-muted-foreground">{results.length} lender{results.length === 1 ? "" : "s"} found</div>

      <div className="mt-3 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {results.map((l) => {
          const meta = recommendationMeta[l.recommendation];
          return (
            <Link
              key={l.slug}
              to="/lenders/$slug"
              params={{ slug: l.slug }}
              className="group rounded-2xl border border-border bg-card p-5 shadow-soft transition hover:-translate-y-0.5 hover:shadow-elevated"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-display text-lg font-semibold">{l.name}</div>
                  <div className="text-xs text-muted-foreground">{l.category} · {l.hq}</div>
                </div>
                <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest ${meta.bg} ${meta.tone}`}>
                  {meta.label}
                </span>
              </div>

              <p className="mt-3 line-clamp-2 text-sm text-muted-foreground">{l.summary}</p>

              <div className="mt-5 grid grid-cols-4 gap-2 text-center">
                {[
                  { label: "Trust", v: l.scores.trust },
                  { label: "Predatory", v: l.scores.predatory, invert: true },
                  { label: "Sentiment", v: l.scores.sentiment },
                  { label: "Complaints", v: l.scores.complaints, invert: true },
                ].map((s) => (
                  <div key={s.label} className="rounded-md bg-muted py-2 text-xs">
                    <div className="text-muted-foreground">{s.label}</div>
                    <div className="font-semibold tabular-nums">{s.invert ? 100 - s.v : s.v}</div>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                <span>APR {l.aprRange[0]}–{l.aprRange[1]}%</span>
                <span>{l.reviewCount.toLocaleString()} reviews</span>
              </div>
            </Link>
          );
        })}
      </div>
    </AppShell>
  );
}
